import { Button, Chip, Switch } from "@heroui/react";
import { Segment } from "@heroui-pro/react";
import { ArrowRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { ScrapedCategoryViewModal } from "../modal/view";

import {
  ColumnsMenu,
  ComboField,
  DataTable,
  InlineFilterSelect,
  InitialsTile,
  SearchBox,
  notify,
  type DataGridColumn,
} from "@/components/kit";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useUpdateScrapedCategory } from "@/hooks/api/scrape-category/useUpdateScrapeCategory";
import { useGetAllSuppliers } from "@/hooks/api/supplier";
import { ScrapedCategory } from "@/types/scrape-category";

export interface ScrapedCategoryTableProps {
  items: ScrapedCategory[];
  page: number;
  totalCategories: number;
  totalPage: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  searchTerm: string;
  handleSearchTermChange: (searchTerm: string) => void;
  supplierId: string;
  handleSupplierIdChange: (supplierId: string) => void;
  blacklistFilter: string;
  handleBlacklistFilterChange: (value: string) => void;
  limit: number;
  handleLimitChange: (limit: number) => void;
}

const COLUMN_LABELS = [
  { id: "name", label: "Scraped category" },
  { id: "linkedCategoryId", label: "Linked category" },
  { id: "brandsCount", label: "Brands" },
  { id: "blacklist", label: "Blacklisted" },
  { id: "actions", label: "Actions" },
];

const BLACKLIST_TABS = [
  { id: "all", label: "All" },
  { id: "whitelisted", label: "Whitelisted" },
  { id: "blacklisted", label: "Blacklisted" },
];

export const ScrapedCategoryTable = ({
  items,
  page,
  totalCategories,
  changePage,
  isLoading,
  searchTerm,
  handleSearchTermChange,
  supplierId,
  handleSupplierIdChange,
  blacklistFilter,
  handleBlacklistFilterChange,
  limit,
  handleLimitChange,
}: ScrapedCategoryTableProps) => {
  const [visibleColumns, setVisibleColumns] = useState(
    () => new Set(COLUMN_LABELS.map((column) => column.id)),
  );
  const [selectedCategory, setSelectedCategory] =
    useState<ScrapedCategory | null>(null);

  const { data: suppliers } = useGetAllSuppliers();
  const { data: categories } = useGetAllCategories();

  const { mutate: updateScrapedCategory } = useUpdateScrapedCategory();

  const handleUpdateBlacklist = useCallback(
    (category: ScrapedCategory, newValue: boolean) => {
      updateScrapedCategory(
        { id: category.id, blacklist: newValue },
        {
          onError: (error: any) => {
            notify.error(
              `Failed to update category ${category.name}`,
              error.response?.data?.message ||
                "Failed to update blacklist status. Please try again.",
            );
          },
        },
      );
    },
    [updateScrapedCategory],
  );

  const handleUpdateLinkedCategory = useCallback(
    (category: ScrapedCategory, linkedCategoryId: string | null) => {
      if (category.id === selectedCategory?.id) {
        setSelectedCategory({ ...category, linkedCategoryId });
      }

      updateScrapedCategory(
        { id: category.id, linkedCategoryId },
        {
          onError: (error: any) => {
            notify.error(
              `Failed to update category ${category.name}`,
              error.response?.data?.message ||
                "Failed to update linked category. Please try again.",
            );
          },
        },
      );
    },
    [updateScrapedCategory, selectedCategory?.id],
  );

  // The cells are rebuilt whenever the supplier and category lists arrive, so a row can never keep the empty list it
  // was first drawn with.
  const allColumns = useMemo<DataGridColumn<ScrapedCategory>[]>(
    () => [
      {
        id: "name",
        header: "Scraped category",
        isRowHeader: true,
        minWidth: 240,
        cell: (category) => {
          const supplier = suppliers?.find((s) => s.id === category.supplierId);

          return (
            <div className="flex min-w-0 items-center gap-3">
              <InitialsTile name={category.name} />
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="flex items-center gap-2">
                  <span className="truncate font-medium">{category.name}</span>
                  {category.blacklist ? (
                    <Chip color="danger" size="sm" variant="soft">
                      Blacklisted
                    </Chip>
                  ) : null}
                </span>
                <span className="text-muted truncate text-xs">
                  {supplier?.name || "Unknown Supplier"}
                  <span className="sr-only"> {category.supplierId}</span>
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "arrow",
        header: <span className="sr-only">Links to</span>,
        width: 36,
        cell: () => (
          <ArrowRight aria-hidden="true" className="text-muted size-4" />
        ),
      },
      {
        id: "linkedCategoryId",
        header: "Linked category",
        width: 230,
        cell: (category) => (
          <ComboField
            hideLabel
            inline
            items={categories ?? []}
            label={`Linked category for ${category.name}`}
            placeholder="Not linked"
            value={category.linkedCategoryId ?? ""}
            onChange={(id) => handleUpdateLinkedCategory(category, id || null)}
          />
        ),
      },
      {
        id: "brandsCount",
        header: "Brands",
        width: 170,
        cell: (category) => {
          const total = category.brandRelations.length;
          const blocked = category.brandRelations.filter(
            (rel) => rel.blacklist || rel.brand.blacklist,
          ).length;
          const whitelisted = total - blocked;

          return (
            <div className="flex flex-col leading-tight">
              <span className="font-medium tabular-nums">{total}</span>
              <span className="text-muted text-xs tabular-nums">
                {total === 0
                  ? "No brands scraped"
                  : blocked === 0
                    ? `${whitelisted} whitelisted`
                    : `${whitelisted} whitelisted · ${blocked} blocked`}
              </span>
            </div>
          );
        },
      },
      {
        id: "blacklist",
        header: "Blacklisted",
        width: 110,
        cell: (category) => (
          <Switch
            aria-label={`Blacklisted: ${category.name}`}
            isSelected={category.blacklist}
            size="sm"
            onChange={(selected) => handleUpdateBlacklist(category, selected)}
          >
            <Switch.Content>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Content>
          </Switch>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 220,
        align: "end",
        cell: (category) => (
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onPress={() => setSelectedCategory(category)}
            >
              View
            </Button>
            <a
              className="button button--outline button--sm"
              href={`/scraped/products?categoryIds=${category.id}:${encodeURIComponent(category.name)}`}
              rel="noreferrer"
              target="_blank"
            >
              Browse products
            </a>
          </div>
        ),
      },
    ],
    [suppliers, categories, handleUpdateBlacklist, handleUpdateLinkedCategory],
  );

  const columns = allColumns.filter((column) =>
    column.id === "arrow"
      ? visibleColumns.has("linkedCategoryId")
      : visibleColumns.has(column.id),
  );

  const hasFilters = Boolean(searchTerm || supplierId || blacklistFilter);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <Segment
          aria-label="Blacklist status"
          selectedKey={blacklistFilter || "all"}
          onSelectionChange={(key) =>
            handleBlacklistFilterChange(key === "all" ? "" : String(key))
          }
        >
          {BLACKLIST_TABS.map((tab, index) => (
            <Segment.Item key={tab.id} id={tab.id}>
              {index > 0 ? <Segment.Separator /> : null}
              {tab.label}
            </Segment.Item>
          ))}
        </Segment>
        <div className="flex min-w-0 max-w-full items-center gap-2">
          <div>
            <InlineFilterSelect
              allLabel="All"
              label="Supplier"
              options={(suppliers ?? []).map((s) => ({
                id: s.id,
                label: s.name,
              }))}
              value={supplierId || null}
              onChange={(value) => handleSupplierIdChange(value ?? "")}
            />
          </div>
          <SearchBox
            className="w-64"
            placeholder="Search by name"
            value={searchTerm}
            onChange={handleSearchTermChange}
          />
          <ColumnsMenu
            columns={COLUMN_LABELS}
            visible={visibleColumns}
            onChange={setVisibleColumns}
          />
        </div>
      </div>

      <DataTable
        compact
        columns={columns}
        empty={{
          title: "No scraped categories found",
          description: "Try adjusting your search or filters.",
          action: hasFilters ? (
            <Button
              size="sm"
              variant="secondary"
              onPress={() => {
                handleSearchTermChange("");
                handleSupplierIdChange("");
                handleBlacklistFilterChange("");
              }}
            >
              Clear all filters
            </Button>
          ) : undefined,
        }}
        getRowId={(category) => category.id}
        isLoading={isLoading && items.length === 0}
        label="Scraped categories"
        minTableWidth={960}
        pagination={{
          page,
          pageSize: limit,
          total: totalCategories,
          onPageChange: changePage,
          onPageSizeChange: handleLimitChange,
          itemLabel: "categories",
        }}
        rows={items}
      />

      <ScrapedCategoryViewModal
        category={selectedCategory}
        isOpen={!!selectedCategory}
        setCategory={setSelectedCategory}
        onClose={() => setSelectedCategory(null)}
      />
    </div>
  );
};
